import React, { useState, useEffect } from 'react';
import { obterIdUsuario } from '../../services/seguranca';
import { useAppSelector } from '../../store/hooks';
import api from '../../services/api';
import { toast } from 'react-toastify';

interface SegurancaTabProps {
    email: string;
}

const SegurancaTab: React.FC<SegurancaTabProps> = ({ email }) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [passwordStrength, setPasswordStrength] = useState<{ score: number, message: string }>({ score: 0, message: '' });
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [token] = useState(useAppSelector(state => state.token));

    const tratarErro = (error: any) => {
        const errorMessage = error.response?.data?.Mensagem || 'Erro desconhecido';
        toast.error('Não foi possível continuar a operação: ' + errorMessage);
    }

    const validatePassword = (password: string) => {
        let score = 0;
        let messages = [];

        if (password.length >= 8) score += 1;
        else messages.push('Pelo menos 8 caracteres');

        if (/\d/.test(password)) score += 1;
        else messages.push('Pelo menos 1 número');

        if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score += 1;
        else messages.push('Pelo menos 1 caractere especial');

        if (/[A-Z]/.test(password)) score += 1;
        else messages.push('Pelo menos 1 letra maiúscula');

        const strengthMessages = [
            'Muito fraca',
            'Fraca',
            'Moderada',
            'Forte',
            'Muito forte'
        ];

        return {
            score,
            message: score >= 4 ? strengthMessages[score] : `${strengthMessages[score]}: ${messages.join(', ')}`
        };
    };

    useEffect(() => {
        if (newPassword) {
            setPasswordStrength(validatePassword(newPassword));
        } else {
            setPasswordStrength({ score: 0, message: '' });
        }
    }, [newPassword]);

    const validateFields = () => {
        const newErrors: { [key: string]: string } = {};
        if (!currentPassword) newErrors.currentPassword = 'Senha atual é obrigatória';
        if (!newPassword) newErrors.newPassword = 'Nova senha é obrigatória';
        if (!confirmPassword) newErrors.confirmPassword = 'Confirmação da senha é obrigatória';
        if (newPassword && confirmPassword && newPassword !== confirmPassword) {
            newErrors.confirmPassword = 'As senhas não coincidem';
        }
        if (newPassword && passwordStrength.score < 3) {
            newErrors.newPassword = 'Senha muito fraca';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleUpdateSecurity = async () => {
        try {
            if (validateFields()) {
                const idUsuario = obterIdUsuario(token);
                await api.put('/Usuario/alterarsenha', {
                    idUsuario: obterIdUsuario(token),
                    senhaAtual: currentPassword,
                    novaSenha: confirmPassword
                });
                setErrors({});
                toast.success("A sua senha foi alterada com sucesso");
            }
        } catch (error) {
            tratarErro(error);
        }
    };

    const getPasswordStrengthColor = () => {
        switch (passwordStrength.score) {
            case 0: return 'bg-gray-200';
            case 1: return 'bg-red-500';
            case 2: return 'bg-yellow-500';
            case 3: return 'bg-blue-500';
            case 4: return 'bg-green-500';
            default: return 'bg-gray-200';
        }
    };

    const handleEmailChangeRequest = () => {
        const message = `Solicito alteração do email: ${email}`;
        const whatsappUrl = `https://wa.me/5585991491356?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
    };

    return (
        <div className="space-y-4 bg-white dark:bg-gray-900 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-black dark:text-gray-100 mb-4">Configurações de Segurança</h2>

            <div className="space-y-1 max-w-3xl">
                <label className="block text-sm font-medium text-black dark:text-gray-200">
                    E-mail
                </label>
                <input
                    type="email"
                    value={email}
                    disabled
                    className="w-full p-2 border border-gray rounded-md bg-gray-100 dark:bg-gray text-gray-500 dark:text-gray-400 cursor-not-allowed"
                />
                <br />
                <button
                    onClick={handleEmailChangeRequest}
                    className="text-sm text-[#1F467E] hover:underline mt-1 focus:outline-none"
                >
                    Solicitar alteração de e-mail
                </button>
            </div>

            <div className="pt-4">
                <h3 className="text-lg font-medium text-black dark:text-gray-100 mb-3">Alterar senha</h3>

                <div className="space-y-4 max-w-3xl">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                        <div className="space-y-1">
                            <label className="block text-sm font-medium text-black dark:text-gray-200">
                                Senha atual
                            </label>
                            <div className="relative w-full">
                                <input
                                    type={showCurrentPassword ? "text" : "password"}
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    className="w-full p-2 border border-gray rounded-md bg-white dark:bg-black text-black dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#1F467E] focus:border-transparent pr-10"
                                    placeholder="Digite sua senha atual"
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                >
                                    {showCurrentPassword ? (
                                        <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    ) : (
                                        <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            {errors.currentPassword && <p className="text-red-500 text-sm">{errors.currentPassword}</p>}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                        <div className="space-y-1">
                            <label className="block text-sm font-medium text-black dark:text-gray-200">
                                Nova senha
                            </label>
                            <div className="relative">
                                <input
                                    type={showNewPassword ? "text" : "password"}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="w-full p-2 border border-gray rounded-md bg-white dark:bg-black text-black dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#1F467E] focus:border-transparent pr-10"
                                    placeholder="Digite a nova senha"
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                >
                                    {showNewPassword ? (
                                        <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    ) : (
                                        <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            {errors.newPassword && <p className="text-red-500 text-sm">{errors.newPassword}</p>}

                            {newPassword && (
                                <div className="mt-2">
                                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                                        <div
                                            className={`h-2.5 rounded-full ${getPasswordStrengthColor()}`}
                                            style={{ width: `${(passwordStrength.score / 4) * 100}%` }}
                                        ></div>
                                    </div>
                                    <p className="text-xs mt-2 text-black dark:text-gray-300">
                                        {passwordStrength.message}
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="space-y-1">
                            <label className="block text-sm font-medium text-black dark:text-gray-200">
                                Confirme a nova senha
                            </label>
                            <div className="relative">
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="w-full p-2 border border-gray rounded-md bg-white dark:bg-black text-black dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#1F467E] focus:border-transparent pr-10"
                                    placeholder="Confirme a nova senha"
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? (
                                        <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    ) : (
                                        <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            {errors.confirmPassword && <p className="text-red-500 text-sm">{errors.confirmPassword}</p>}
                        </div>
                    </div>
                </div>
            </div>

            <div className="pt-4">
                <button
                    onClick={handleUpdateSecurity}
                    className="px-4 py-2 bg-[#1F467E] text-white rounded-md hover:bg-[#2a5a9e] transition-colors focus:outline-none focus:ring-2 focus:ring-[#1F467E] focus:ring-offset-2"
                >
                    Atualizar senha
                </button>
            </div>
        </div>
    );
};

export default SegurancaTab;